import { useRestPostQuery } from 'utils/hooks/api/useRestPostQuery';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { ApiResponse } from 'services/api';
import {
  BaseLevelBreakdownApiRequest,
  BaseLevelBreakdownApiResp,
  StoredCampaignBreakdowns,
  StoredSnippetBreakdowns,
} from '../../types';

const BASE_LEVEL_BREAKDOWN_URL = '/analytics/snippets/baseLevel/breakdown';

export const useQueryBaseLevelBreakdown = (
  {
    campaignIds,
    startTime,
    endTime,
    baseLevelSnippetIds,
    showCampaignBreakdown,
    templateIdOpt,
  }: BaseLevelBreakdownApiRequest,
  setSnippetStoredBreakdowns: Dispatch<SetStateAction<StoredSnippetBreakdowns>>,
  setCampaignStoredBreakdowns: Dispatch<SetStateAction<StoredCampaignBreakdowns>>,
) => {
  const params = useMemo(
    () => ({
      campaignIds,
      startTime,
      endTime,
      baseLevelSnippetIds,
      showCampaignBreakdown,
      templateIdOpt,
    }),
    [campaignIds, startTime, endTime, baseLevelSnippetIds, showCampaignBreakdown, templateIdOpt],
  );

  const handleResponse = useCallback((response: ApiResponse<BaseLevelBreakdownApiResp>) => {
    const { data = { results: { snippetBreakdown: {}, campaignBreakdown: {} } } } = response;
    const {
      results: { snippetBreakdown, campaignBreakdown },
    } = data;
    setSnippetStoredBreakdowns((prev) => ({ ...prev, ...snippetBreakdown }));
    setCampaignStoredBreakdowns((prev) => ({ ...prev, ...campaignBreakdown }));
    return data;
  }, []);

  const {
    data = { results: { snippetBreakdown: {}, campaignBreakdown: {} } },
    isError: hasBreakdownError,
  } = useRestPostQuery<BaseLevelBreakdownApiResp, BaseLevelBreakdownApiRequest>(
    BASE_LEVEL_BREAKDOWN_URL,
    params,
    {
      enabled: campaignIds.length > 0 && !!startTime && !!endTime && baseLevelSnippetIds.length > 0,
    },
    undefined,
    handleResponse,
  );

  return { snippetBreakdownData: data.results.snippetBreakdown, hasBreakdownError };
};
